import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { IosKeyboardFixDirective, KEYBOARD_TOKEN, KeyboardProvider } from '@core';
import { QuotedMessage } from '../../../application/interfaces';
import { MessageInputViewService } from './view-service/message-input.view-service';
import { MentionAutocompleteComponent, MentionMember } from './mention-autocomplete/mention-autocomplete.component';

@Component({
  selector: 'lib-custom-message-input',
  templateUrl: './custom-message-input.component.html',
  styleUrls: ['./custom-message-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule, IonicModule, MentionAutocompleteComponent],
  providers: [MessageInputViewService],
  hostDirectives: [IosKeyboardFixDirective]
})
export class CustomMessageInputComponent {
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MentionAutocompleteComponent) mentionAutocomplete?: MentionAutocompleteComponent;

  private readonly viewService: MessageInputViewService = inject(MessageInputViewService);
  private readonly keyboard: KeyboardProvider = inject(KEYBOARD_TOKEN);

  readonly messageText: WritableSignal<string> = this.viewService.messageText;
  readonly imagePreview: Signal<string | null> = this.viewService.imagePreview;
  readonly quotedMessage: Signal<QuotedMessage | null> = this.viewService.quotedMessage;
  readonly isEditing: Signal<boolean> = this.viewService.isEditing;
  readonly canSend: Signal<boolean> = this.viewService.canSend;
  readonly showMentionAutocomplete: WritableSignal<boolean> = this.viewService.showMentionAutocomplete;
  readonly mentionFilterText: WritableSignal<string> = this.viewService.mentionFilterText;
  readonly channelMembers: MentionMember[] = this.viewService.getChannelMembers();

  openFilePicker(): void {
    this.fileInput?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const file: File | undefined = input.files?.[0];

    if (file && file.type.startsWith('image/')) {
      this.viewService.selectImage(file);
    }

    input.value = '';
  }

  removeSelectedImage(): void {
    this.viewService.removeSelectedImage();
  }

  sendMessage(): void {
    this.viewService.sendMessage(() => {
      this.resetTextareaHeight();
      this.hideKeyboard();
    });
  }

  cancelQuote(): void {
    this.viewService.cancelQuote();
  }

  cancelEdit(): void {
    this.viewService.cancelEdit(() => this.resetTextareaHeight());
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.showMentionAutocomplete() && this.mentionAutocomplete) {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.mentionAutocomplete.moveUp();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.mentionAutocomplete.moveDown();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        this.mentionAutocomplete.confirmSelection();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.viewService.closeMentionAutocomplete();
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onInput(): void {
    this.resizeTextarea();
    const textarea: HTMLTextAreaElement = this.messageInput.nativeElement;
    this.viewService.handleMentionInput(textarea.value, textarea.selectionStart || 0);
  }

  onMentionSelected(member: MentionMember): void {
    const textarea: HTMLTextAreaElement = this.messageInput.nativeElement;
    const result: { text: string; newCursorPosition: number } = this.viewService.insertMention(
      member,
      textarea.value,
      textarea.selectionStart || 0
    );
    this.messageText.set(result.text);

    setTimeout(() => {
      textarea.selectionStart = result.newCursorPosition;
      textarea.selectionEnd = result.newCursorPosition;
      textarea.focus();
    });
  }

  private resizeTextarea(): void {
    if (this.messageInput?.nativeElement) {
      const textarea: HTMLTextAreaElement = this.messageInput.nativeElement;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }

  private resetTextareaHeight(): void {
    if (this.messageInput?.nativeElement) {
      this.messageInput.nativeElement.style.height = 'auto';
    }
  }

  private hideKeyboard(): void {
    this.messageInput?.nativeElement?.blur();
    this.keyboard.hide$().subscribe();
  }
}
